import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
  useEffect,
  useRef,
} from 'react';
import { useLongPress } from 'use-long-press';
import { useResizeObserver } from 'usehooks-ts';
import SelectableListItem from '@/components/common/selectable-list/selectable-list-item';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';
import MessageContent from './message-content/message-content';
import useMessageInfo from './use-message-info';

interface Props {
  className?: string;
  isHighlighted?: boolean;
  showHeader?: boolean;
  type?: 'quote' | 'clamped';
  message: ConversationMessageTypeUnion;

  onResize?: () => void;
}

export default function MessageListItem({
  message,
  type,
  showHeader = true,
  isHighlighted,
  className,
  onResize,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();
  const { isMine, canSummonContextMenu } = useMessageInfo(message);
  const { selectedMessages, toggleSelectedMessage, multiselectAction, resetSelectedMessages } = useConversationStore();
  const { handleInteraction } = useConversationHandlers();

  const isSelected = selectedMessages.some((m) => m.ID === message.ID);

  const handleContextMenu = (e: MouseEvent | ReactMouseEvent | ReactTouchEvent | ReactPointerEvent) => {
    e.preventDefault();
    resetSelectedMessages([message]);

    let position: [number, number] = [0, 0];
    if ('clientX' in e) {
      position = [e.clientX, e.clientY];
    } else if ('touches' in e && e.touches.length > 0) {
      const touch = e.touches.item(0);
      position = [touch.clientX, touch.clientY];
    }
    handleInteraction('contextmenu', position);
  };

  const bindLongPress = useLongPress((e) => handleContextMenu(e), { threshold: 800 });

  useResizeObserver({ ref, onResize, box: 'border-box' });
  useEffect(() => {
    ref.current?.addEventListener('contextmenu', handleContextMenu);

    return () => ref.current?.removeEventListener('contextmenu', handleContextMenu);
  }, [message]);

  return (
    <div
      ref={ref}
      key={`message-${message.ID}`}
      id={`message-${message.ID}`}
      data-side={isMine ? 'sender' : 'receiver'}
      className={cn(
        'flex max-w-full select-none desktop:select-text desktop:selection:bg-blue-200 items-center transition group px-5 py-3',
        {
          'bg-metal-100': isHighlighted,
        },
        className,
      )}
      {...(canSummonContextMenu && !isDesktop ? bindLongPress() : {})}>
      <SelectableListItem
        isSelected={isSelected}
        isSelectable={multiselectAction !== null}
        onSelected={() => toggleSelectedMessage(message)}>
        <MessageContent
          showHeader={showHeader}
          message={message}
          type={type}
        />
      </SelectableListItem>
    </div>
  );
}
